import React, { useState, useEffect } from "react";
import axios from "axios";
import { TaskChatModal, acceptBlock, participantRole } from "../../components/task/TaskChatModal";
import { TaskStatusBadge } from "../../components/task/TaskStatusBadge";
import { getTaskCardClasses } from "../../utils/taskStyles";
import { updateFaviconBadge } from "../../App";
import { useModalHistory } from "../../utils/historyNav";
import { PaginationControls } from "../../components/ui/PaginationControls";
import { needsSurcharge, fmtRub } from "../../utils/priceChange";
import { useSingleFlight } from "../../utils/singleFlight";
import { CancelWithRefundButton } from "../../components/task/CancelWithRefundButton";
import { MessageSquare, MapPin, Wallet, User, CalendarDays, CreditCard, ArrowUpDown, ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DiscussionsList = ({ user, showToast, initialOpenTaskId, onInitialChatOpened }) => {
 const [discussions, setDiscussions] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedTask, setSelectedTask] = useState(null);
 const { run: runOnce, isBusy } = useSingleFlight();
 const [pendingOpenTaskId, setPendingOpenTaskId] = useState(initialOpenTaskId || null);

 // Сортировка и пагинация
 const [sortBy, setSortBy] = useState('date_desc');
 const [currentPage, setCurrentPage] = useState(1);
 const [perPage, setPerPage] = useState(10);

 // Чат-модал создаёт запись истории: жест "назад" закрывает чат, а не выбрасывает из приложения
 useModalHistory(!!selectedTask, () => setSelectedTask(null));


 useEffect(() => {
 fetchDiscussions();
 const interval = setInterval(fetchDiscussions, 5000);
 return () =>clearInterval(interval);
 }, []);

 // Deep-link из email: автоматически открываем чат нужного задания после загрузки списка
 useEffect(() => {
 if (!pendingOpenTaskId || loading) return;
 const task = discussions.find(d => d.id === pendingOpenTaskId);
 if (task) {
 setSelectedTask(task);
 markAsRead(task.id);
 } else {
 showToast("Чат по этому заданию не найден среди ваших обсуждений", "info");
 }
 setPendingOpenTaskId(null);
 if (onInitialChatOpened) onInitialChatOpened();
 }, [pendingOpenTaskId, loading, discussions]);

 const fetchDiscussions = async () => {
 try {
 const response = await axios.get(`${API}/my-discussions`);
 setDiscussions(response.data);
 } catch (error) {
 console.error("Error fetching discussions:", error);
 showToast("Ошибка при загрузке обсуждений", "error");
 } finally {
 setLoading(false);
 }
 };

 const markAsRead = async (taskId) => {
 try {
 await axios.post(`${API}/tasks/${taskId}/messages/mark-read`);
 updateFaviconBadge(0);
 // Refresh discussions to update unread counts
 fetchDiscussions();
 } catch (error) {
 console.error("Error marking messages as read:", error);
 }
 };

 const getStatusBadge = (task) => <TaskStatusBadge task={task} />;
 const getParticipantRole = (task) => participantRole(task, user);

 if (loading) {
 return (
 <div className="flex flex-col items-center justify-center py-20 gap-3">
 <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin" style={{borderColor:'var(--rv-teal)', borderTopColor:'transparent'}} />
 <div style={{color:'var(--rv-text-muted)'}}>Загрузка обсуждений...</div>
 </div>
 );
 }

 const getSortedDiscussions = () => {
  const sorted = [...discussions];
  switch (sortBy) {
   case 'date_desc': return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
   case 'date_asc':  return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
   case 'unread':    return sorted.sort((a, b) => (b.unread_count || 0) - (a.unread_count || 0));
   case 'price_desc': return sorted.sort((a, b) => b.price - a.price);
   case 'price_asc':  return sorted.sort((a, b) => a.price - b.price);
   default: return sorted;
  }
 };

 const sortedDiscussions = getSortedDiscussions();
 const paginatedDiscussions = sortedDiscussions.slice((currentPage - 1) * perPage, currentPage * perPage);

 return (
 <div className="space-y-6">
 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
  <h2 className="text-2xl font-bold flex items-center gap-2" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
   <MessageSquare size={22} style={{color:'var(--rv-teal)'}} />Мои обсуждения
  </h2>
  <div className="flex items-center gap-2 flex-wrap">
   <span className="text-sm" style={{color:'var(--rv-text-muted)'}}>
    {discussions.length} обсуждений
   </span>
   {discussions.length > 1 && (
    <div className="flex items-center gap-1.5">
     <ArrowUpDown size={13} style={{color:'var(--rv-text-muted)'}} />
     <select
      data-testid="discussions-sort-select"
      value={sortBy}
      onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
      className="text-sm px-2 py-1 rounded-md"
      style={{border:'1px solid var(--rv-border)', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif', background:'white'}}
     >
      <option value="date_desc">Дата: новые</option>
      <option value="date_asc">Дата: старые</option>
      <option value="unread">Непрочитанные</option>
      <option value="price_desc">Цена: высокая</option>
      <option value="price_asc">Цена: низкая</option>
     </select>
    </div>
   )}
  </div>
 </div>

 {discussions.length === 0 ? (
 <div className="text-center py-8">
 <div className="mb-4" style={{color:'var(--rv-text-secondary)'}}>
 <div className="flex justify-center mb-4">
 <MessageSquare size={48} style={{color:'var(--rv-border)'}} />
 </div>
 <p className="text-lg mb-2" style={{color:'var(--rv-navy)', fontFamily:'Manrope,sans-serif'}}>Пока нет активных обсуждений</p>
 <p className="text-sm">
 {(user.role === "customer" || user.role === "admin")
 ? "Создайте задание и ждите заинтересованных исполнителей"
 : "Нажмите 'Ознакомиться' на интересующем задании, чтобы начать обсуждение"}
 </p>
 </div>
 </div>
 ) : (<>
 <div className="space-y-4">
 {paginatedDiscussions.map(task => {
 const isExecDiscussing = user.role === "executor" && task.status === "discussing" && task.discussing_executor_id === user.id;
 const isCustDiscussing = (user.role === "customer" || user.role === "admin") && task.status === "discussing" && task.customer_id === user.id;
 const block = isExecDiscussing ? acceptBlock(task) : null;
 const openChat = () => { setSelectedTask(task); markAsRead(task.id); };
 return (
 <div key={task.id} data-testid={`discussion-card-${task.id}`} className={`${getTaskCardClasses(task)} border hover:shadow-lg transition-shadow duration-200`}>
 {/* Ж23: бейджи над заголовком — без мёртвой зоны справа */}
 <div className="flex items-center gap-2 flex-wrap mb-2" data-testid={`discussion-card-badges-${task.id}`}>
 {getStatusBadge(task)}
 {task.unread_count > 0 && (
 <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full" data-testid={`discussion-card-unread-${task.id}`}>
 {task.unread_count} новых
 </span>
 )}
 </div>
 <h3 
 data-testid={`discussion-card-title-${task.id}`}
 className="w-full text-lg font-semibold mb-0.5 cursor-pointer transition-colors break-words leading-snug flex items-start gap-1 group hover:underline underline-offset-4"
 style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif', textDecorationColor:'var(--rv-teal)', overflowWrap:'break-word', wordBreak:'normal', hyphens:'auto'}}
 onClick={openChat}
 title="Открыть чат по заданию"
 >
 <span className="min-w-0">{task.title}</span>
 <ArrowUpRight size={16} className="flex-shrink-0 mt-1 opacity-60 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
 </h3>
 <p className="text-xs mb-1" style={{color:'var(--rv-text-muted)'}}>Нажмите на название, чтобы открыть чат</p>
 <p className="text-sm mb-2 flex items-start gap-1 break-words" style={{color:'var(--rv-text-secondary)'}}>
 <MapPin size={13} style={{color:'var(--rv-text-muted)'}} className="flex-shrink-0 mt-0.5" />
 <span className="min-w-0">{task.location}</span>
 </p>
 
 {/* P1: Индикатор доплаты для заказчика (только эскроу-задания) */}
 {user.id === task.customer_id && needsSurcharge(task, task.active_price_change) && (
 <div className="inline-flex items-center space-x-1 bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-1 rounded-full mb-2" data-testid={`discussion-surcharge-badge-${task.id}`}>
 <CreditCard size={11} />
 <span>Требуется доплата {fmtRub(task.active_price_change.new_price - task.active_price_change.old_price)} ₽</span>
 </div>
 )}

 <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm mb-3" style={{color:'var(--rv-text-secondary)'}}>
 <span className="flex items-center gap-1 font-semibold" style={{color:'var(--rv-navy)'}}><Wallet size={13} style={{color:'var(--rv-text-muted)'}} />{fmtRub(task.price)} ₽</span>
 <span className="flex items-center gap-1"><User size={13} style={{color:'var(--rv-text-muted)'}} />Вы: {getParticipantRole(task)}</span>
 <span className="flex items-center gap-1"><CalendarDays size={13} style={{color:'var(--rv-text-muted)'}} />{new Date(task.updated_at).toLocaleDateString('ru-RU')}</span>
 </div>

 {/* Ж23: единая сетка кнопок одинаковой высоты — «Открыть чат» на всю ширину, пара действий 50/50 (на десктопе — три в ряд) */}
 <div className={`grid grid-cols-2 ${isExecDiscussing ? 'sm:grid-cols-3' : ''} gap-2 pt-3 border-t`} style={{borderColor:'var(--rv-border)'}} data-testid={`discussion-card-actions-${task.id}`}>
 <button
 data-testid={`discussion-open-chat-${task.id}`}
 onClick={openChat}
 className={`reversia-btn reversia-btn-teal reversia-btn-md click-effect justify-center min-h-[44px] ${isCustDiscussing ? '' : 'col-span-2'} ${isExecDiscussing ? 'sm:col-span-1' : ''}`}
 >Открыть чат
 {task.unread_count > 0 && (
 <span className="text-xs px-2 py-0.5 rounded-full font-bold bounce-in" style={{background:'rgba(255,255,255,0.22)'}}>
 {task.unread_count}
 </span>
 )}
 </button>
 
 {isExecDiscussing && (
 <>
 {/* Ж20: кнопка видна всегда, но неактивна при активном proposed / неоплаченной доплате / серверной причине */}
 <button
 data-testid={`discussion-accept-task-${task.id}`}
 onClick={() => !block && runOnce(`accept:${task.id}`, async () => {
 try {
 await axios.post(`${API}/tasks/${task.id}/accept`);
 showToast("Задание принято!", "success");
 fetchDiscussions();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при принятии задания", "error");
 }
 })}
 disabled={!!block || isBusy(`accept:${task.id}`)}
 aria-disabled={!!block}
 title={block ? block.text : undefined}
 className="reversia-btn reversia-btn-green reversia-btn-md click-effect justify-center min-h-[44px]"
 style={block ? {opacity:0.5, cursor:'not-allowed'} : undefined}
 >Принять
 </button>
 <button
 data-testid={`discussion-decline-task-${task.id}`}
 onClick={async () => {
 if (window.confirm("Вы уверены что хотите отказаться от этого задания? Оно станет доступно другим исполнителям.")) {
 try {
 await axios.post(`${API}/tasks/${task.id}/cancel-discussion`);
 showToast("Вы отказались от задания", "info");
 fetchDiscussions();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при отказе", "error");
 }
 }
 }}
 className="reversia-btn reversia-btn-quiet reversia-btn-md click-effect justify-center min-h-[44px]"
 >Отказаться
 </button>
 {block && (
 <p className="col-span-2 sm:col-span-3 text-xs px-2.5 py-1.5 rounded-lg" data-testid={`discussion-accept-blocked-${task.id}`}
 style={block.kind === 'surcharge' ? {background:'#FFF7ED', color:'#C2410C', border:'1px solid #FED7AA'} : {background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE'}}>
 {block.text}
 </p>
 )}
 </>
 )}

 {isCustDiscussing && (
 <button
 data-testid={`discussion-cancel-discussion-${task.id}`}
 onClick={async () => {
 if (window.confirm("Вы уверены что хотите отменить обсуждение с этим исполнителем? Задание станет доступно другим исполнителям.")) {
 try {
 await axios.post(`${API}/tasks/${task.id}/cancel-discussion`);
 showToast("Обсуждение отменено", "info");
 fetchDiscussions();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при отмене", "error");
 }
 }
 }}
 className="reversia-btn reversia-btn-quiet reversia-btn-md click-effect justify-center min-h-[44px]"
 >Отменить обсуждение
 </button>
 )}
 {isCustDiscussing && (
 <CancelWithRefundButton task={task} showToast={showToast} onDone={fetchDiscussions}
 testId={`discussion-cancel-task-${task.id}`}
 className="reversia-btn reversia-btn-danger-soft reversia-btn-md click-effect justify-center min-h-[44px]" />
 )}
 </div>
 </div>
 );
 })}
 </div>
 <PaginationControls
   total={sortedDiscussions.length}
   perPage={perPage}
   currentPage={currentPage}
   onPageChange={setCurrentPage}
   onPerPageChange={setPerPage}
  />
 </>)}

 {/* TZ8-6: единая чат-модаль (эталон раздела «Чат») */}
 {selectedTask && (
 <TaskChatModal
 task={discussions.find(d => d.id === selectedTask.id) || selectedTask}
 user={user}
 showToast={showToast}
 onClose={() => setSelectedTask(null)}
 onChanged={fetchDiscussions}
 onNewMessage={() => fetchDiscussions()}
 />
 )}
 </div>
 );
};
